"use client";

import React from "react";
import { Aperture } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const faqs = [{
  question: "How can I book a photography session?",
  answer: <>
        Booking a session is simple. Please visit our{" "}
        <Link to="/InquirePage" className="text-primary hover:underline">
          Inquire page
        </Link>{" "}
        to fill out the booking form. We specialize in portraiture and editorial work, primarily shooting on black-and-white and color film to capture authentic, timeless moments.
      </>
}, {
  question: "Do you sell fine art prints?",
  answer: "Yes, we offer a curated selection of high-resolution fine art film prints. Each print is produced on archival-quality paper to ensure longevity and fidelity to the original negative. Limited edition runs are available for select works."
}, {
  question: "What equipment do you use?",
  answer: "Our primary kit involves classic 35mm and medium format rangefinders, often favoring Leica and Hasselblad systems. We believe the tactile nature of these mechanical cameras contributes significantly to the soulful, grain-heavy aesthetic of our work."
}, {
  question: "Can I license images for commercial use?",
  answer: "Yes, image licensing is available for commercial projects, publications, and creative collaborations. Please contact us directly with details of your project, and we will provide a tailored quote based on usage and distribution."
}, {
  question: "What is the turnaround time for scans and prints?",
  answer: "Because we prioritize quality and the manual nature of film development, our turnaround time is typically 2-3 weeks for digital scans and up to 4 weeks for custom prints. We ensure every frame is inspected and color-graded to perfection."
}];
export default function FrequentlyAsked() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="frequently-asked" ref={ref} className={cn("dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-16 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-4">
            Frequently Asked
          </h2>
          <p className="text-muted-foreground font-sans max-w-xl mx-auto">
            Insights into our analog process, services, and philosophy.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-2">
          {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10 last:border-0" data-index={i}>
              <AccordionTrigger className="[&>svg:last-child]:hidden py-6 hover:no-underline group">
                <div className="flex items-baseline gap-4 md:gap-8 text-left pr-4">
                  <span className="font-mono text-muted-foreground text-sm md:text-base shrink-0">
                    0{i + 1}.
                  </span>
                  <span className="font-serif text-xl md:text-2xl font-bold leading-tight group-hover:text-primary transition-colors">
                    {faq.question}
                  </span>
                </div>
                <Aperture className="size-6 text-muted-foreground shrink-0 transition-transform duration-500 ease-out group-hover:text-primary" />
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground font-sans text-base leading-relaxed pl-10 md:pl-14 pb-8 max-w-3xl">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}